// NOTE :: The curated timezone list every shelter timezone picker offers. It replaces
// `moment.tz.names()` (597 raw zone ids) with the zones shelters actually sit in. Every Canadian
// province or territory with its own clock has an entry — Vancouver and Edmonton stopped changing
// clocks in 2026 (tzdata 2026b/2026c), so they are no longer the US Pacific / Mountain zones. Yukon
// and Puerto Rico keep their own names even though their clocks match Arizona and Caracas. `value`
// is the IANA id the api stores — never send the label. The array is grouped by region for reading only
// (Americas → Europe → Africa → Middle East & South Asia → East & SE Asia → Oceania); the dropdown
// order comes from `getTimezoneOptions`, which prefixes each label with its UTC offset and sorts by
// it, with `PINNED_TIMEZONES` held at the top. Keep new entries in their region block.
// `label` is an **i18n key**, not display text — @FormModule renders option labels as-is, so
// `getTimezoneOptions` resolves it with the caller's `t`. A new entry needs its key in all three
// locales (`/af-i18n`). A shelter saved on a zone that is not here keeps it: the helper appends it.
const TIMEZONES = [
  // Americas
  { label: 'timezones.pacificHonolulu', value: 'Pacific/Honolulu' },
  { label: 'timezones.americaAnchorage', value: 'America/Anchorage' },
  { label: 'timezones.americaLosAngeles', value: 'America/Los_Angeles' },
  { label: 'timezones.americaVancouver', value: 'America/Vancouver' },
  { label: 'timezones.americaDenver', value: 'America/Denver' },
  { label: 'timezones.americaEdmonton', value: 'America/Edmonton' },
  { label: 'timezones.americaWhitehorse', value: 'America/Whitehorse' },
  { label: 'timezones.americaPhoenix', value: 'America/Phoenix' },
  { label: 'timezones.americaChicago', value: 'America/Chicago' },
  { label: 'timezones.americaRegina', value: 'America/Regina' },
  { label: 'timezones.americaMexicoCity', value: 'America/Mexico_City' },
  { label: 'timezones.americaGuatemala', value: 'America/Guatemala' },
  { label: 'timezones.americaNewYork', value: 'America/New_York' },
  { label: 'timezones.americaBogota', value: 'America/Bogota' },
  { label: 'timezones.americaHalifax', value: 'America/Halifax' },
  { label: 'timezones.americaStJohns', value: 'America/St_Johns' },
  { label: 'timezones.americaCaracas', value: 'America/Caracas' },
  { label: 'timezones.americaPuertoRico', value: 'America/Puerto_Rico' },
  { label: 'timezones.americaManaus', value: 'America/Manaus' },
  { label: 'timezones.americaSantiago', value: 'America/Santiago' },
  { label: 'timezones.americaSaoPaulo', value: 'America/Sao_Paulo' },
  { label: 'timezones.americaArgentinaBuenosAires', value: 'America/Argentina/Buenos_Aires' },

  // Europe
  { label: 'timezones.utc', value: 'UTC' },
  { label: 'timezones.europeLondon', value: 'Europe/London' },
  { label: 'timezones.europeParis', value: 'Europe/Paris' },
  { label: 'timezones.europeBerlin', value: 'Europe/Berlin' },
  { label: 'timezones.europeWarsaw', value: 'Europe/Warsaw' },
  { label: 'timezones.europeAthens', value: 'Europe/Athens' },
  { label: 'timezones.europeIstanbul', value: 'Europe/Istanbul' },
  { label: 'timezones.europeMoscow', value: 'Europe/Moscow' },

  // Africa
  { label: 'timezones.africaLagos', value: 'Africa/Lagos' },
  { label: 'timezones.africaCairo', value: 'Africa/Cairo' },
  { label: 'timezones.africaJohannesburg', value: 'Africa/Johannesburg' },
  { label: 'timezones.africaNairobi', value: 'Africa/Nairobi' },

  // Middle East & South Asia
  { label: 'timezones.asiaJerusalem', value: 'Asia/Jerusalem' },
  { label: 'timezones.asiaRiyadh', value: 'Asia/Riyadh' },
  { label: 'timezones.asiaTehran', value: 'Asia/Tehran' },
  { label: 'timezones.asiaDubai', value: 'Asia/Dubai' },
  { label: 'timezones.asiaKabul', value: 'Asia/Kabul' },
  { label: 'timezones.asiaKarachi', value: 'Asia/Karachi' },
  { label: 'timezones.asiaKolkata', value: 'Asia/Kolkata' },
  { label: 'timezones.asiaKathmandu', value: 'Asia/Kathmandu' },
  { label: 'timezones.asiaDhaka', value: 'Asia/Dhaka' },
  { label: 'timezones.asiaYangon', value: 'Asia/Yangon' },

  // East & Southeast Asia
  { label: 'timezones.asiaBangkok', value: 'Asia/Bangkok' },
  { label: 'timezones.asiaShanghai', value: 'Asia/Shanghai' },
  { label: 'timezones.asiaHongKong', value: 'Asia/Hong_Kong' },
  { label: 'timezones.asiaSingapore', value: 'Asia/Singapore' },
  { label: 'timezones.asiaSeoul', value: 'Asia/Seoul' },
  { label: 'timezones.asiaTokyo', value: 'Asia/Tokyo' },

  // Oceania
  { label: 'timezones.australiaPerth', value: 'Australia/Perth' },
  { label: 'timezones.australiaDarwin', value: 'Australia/Darwin' },
  { label: 'timezones.australiaAdelaide', value: 'Australia/Adelaide' },
  { label: 'timezones.australiaBrisbane', value: 'Australia/Brisbane' },
  { label: 'timezones.australiaSydney', value: 'Australia/Sydney' },
  { label: 'timezones.pacificAuckland', value: 'Pacific/Auckland' },
  { label: 'timezones.pacificFiji', value: 'Pacific/Fiji' },
  { label: 'timezones.pacificApia', value: 'Pacific/Apia' },
  { label: 'timezones.pacificTongatapu', value: 'Pacific/Tongatapu' },
];

// NOTE :: Most shelters are in the US and Canada, so these sit at the top of every timezone
// dropdown in this order (east to west, the way North Americans say them), ahead of the full
// west-to-east list. Each must also exist in `TIMEZONES`; a value missing there is ignored.
const PINNED_TIMEZONES = [
  'America/Halifax',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
];

// NOTE :: Legacy IANA *link* names some shelters were saved on years ago. A link is not a zone of
// its own — it points at a real zone that keeps the identical clock — so the saved id is never
// rewritten and only the label borrows the curated wording. Without this the fallback reads the
// last path segment, and a shelter on `US/Eastern` shows up as "Eastern", one on `Brazil/East` as
// "East". Only the families our own data actually holds are listed; add a link here when one turns
// up, and point it at a zone that is in `TIMEZONES`.
const TIMEZONE_ALIASES: Record<string, string> = {
  'Asia/Calcutta': 'Asia/Kolkata',
  'Brazil/East': 'America/Sao_Paulo',
  'Brazil/West': 'America/Manaus',
  'Canada/Atlantic': 'America/Halifax',
  'Canada/Central': 'America/Chicago',
  'Canada/Eastern': 'America/New_York',
  'Canada/Mountain': 'America/Edmonton',
  'Canada/Newfoundland': 'America/St_Johns',
  'Canada/Pacific': 'America/Vancouver',
  'Canada/Saskatchewan': 'America/Regina',
  'Mexico/General': 'America/Mexico_City',
  'US/Alaska': 'America/Anchorage',
  'US/Arizona': 'America/Phoenix',
  'US/Central': 'America/Chicago',
  'US/East-Indiana': 'America/New_York',
  'US/Eastern': 'America/New_York',
  'US/Hawaii': 'Pacific/Honolulu',
  'US/Michigan': 'America/New_York',
  'US/Mountain': 'America/Denver',
  'US/Pacific': 'America/Los_Angeles',
};

export { PINNED_TIMEZONES, TIMEZONE_ALIASES, TIMEZONES };
